// ⌘K — jump to any screen by name.
//
// This replaces a search box that looked real and did nothing. It searches the
// menu (name, group and the screen's purpose line), so typing "owe", "refund"
// or "vendor" finds the right page even when you don't know its title.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiCornerDownLeft, FiX } from 'react-icons/fi';
import { NAV_ITEMS } from '../../constants/navigation';
import { useWorklist } from '../../hooks/useWorklist';

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { countFor } = useWorklist();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return NAV_ITEMS;
    const words = q.split(/\s+/);
    return NAV_ITEMS
      .map((item) => {
        const name = item.label.toLowerCase();
        const hay = `${name} ${item.section} ${item.purpose}`.toLowerCase();
        if (!words.every((w) => hay.includes(w))) return null;
        // A title match beats a match buried in the description.
        const score = name.startsWith(q) ? 0 : name.includes(q) ? 1 : 2;
        return { item, score };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score)
      .map((r) => r.item);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // Wait for the dialog to mount before stealing focus.
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => { setCursor(0); }, [query]);

  const go = useCallback((item) => {
    if (!item) return;
    onClose();
    navigate(item.path);
  }, [navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[cursor]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Keep the highlighted row in view when arrowing past the fold
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] px-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100">
          <FiSearch className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Go to… try “refund”, “vendor”, “who owes”"
            className="flex-1 text-sm text-slate-800 placeholder-slate-400 bg-transparent focus:outline-none"
          />
          <button onClick={onClose} className="text-slate-300 hover:text-slate-500 shrink-0">
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-2">
          {results.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-slate-400">
              Nothing matches “{query}”.
            </p>
          )}
          {results.map((item, i) => {
            const Icon = item.icon;
            const waiting = item.badge ? countFor(item.badge) : 0;
            return (
              <button
                key={item.path}
                data-idx={i}
                onMouseEnter={() => setCursor(i)}
                onClick={() => go(item)}
                className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                  i === cursor ? 'bg-violet-50' : 'hover:bg-slate-50'
                }`}
              >
                <span className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  i === cursor ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{item.label}</span>
                    {waiting > 0 && (
                      <span className="rounded-full bg-amber-100 border border-amber-200 px-1.5 text-[10px] font-black text-amber-700">
                        {waiting} waiting
                      </span>
                    )}
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-300">
                      {item.section}
                    </span>
                  </span>
                  <span className="block text-xs text-slate-400 truncate">{item.purpose}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4 px-4 py-2 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-sans">↑↓</kbd> move
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-sans">
              <FiCornerDownLeft className="w-3 h-3 inline" />
            </kbd> open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-sans">esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
